import { SELLER_HIGH, SIGNAL_MODEST } from '../blueprints/engine.js';
import {
  CONDITION_LABEL,
  conditionAllows,
  type Condition,
  type ConditionAppliesTo,
  type ConditionToken,
} from './catalog.js';

// ── Conditions engine (pure, isomorphic) ─────────────────────────────────────
// Turns evidence the caller has ALREADY assembled (build pick fields, pool
// stats, buyer context) into rendered conditions. Two hard rules from the spec:
//   • evidence-or-don't-render — a condition with no evidence items is never
//     emitted (§8), so missing data degrades to silence, not to prose;
//   • wording follows the context mode (§7.2) — buyer-relative phrasing
//     ("extends you into") ONLY when a mandate/portfolio is attached; form
//     context speaks about "the build".
// The engine reads the same threshold lines as the blueprint grammar
// (SELLER_HIGH / SIGNAL_MODEST) so the two layers can never disagree on when a
// pattern fires. No DB access, no LLM prose, no new scores.

/** Context the conditions are computed against (spec §7). */
export interface ConditionContext {
  mode: 'buyer_aware' | 'form_context';
  /** Regions the attached portfolio operates in (buyer_aware; empty set = no
   *  owned-footprint conditions). */
  ownedRegions: ReadonlySet<string>;
  /** Regions covered by the REST of the build — lets form-context builds speak
   *  about "extends the build into X" without buyer wording. */
  buildRegions?: ReadonlySet<string>;
  /** Clinical/revenue specialisms the mandate (or form context) is after. */
  mandateSpecialisms?: ReadonlySet<string>;
  /** Mandate sweet-spot deal band, per target, EUR. */
  revMinEur?: number | null;
  revMaxEur?: number | null;
  /** Anchor revenue threshold from the Prescription form (anchor-led builds). */
  anchorMinRevEur?: number | null;
}

/** Everything the target-level rules may read. Callers fill what they have —
 *  a missing field simply keeps the conditions that need it from rendering. */
export interface TargetConditionEvidence {
  role: 'platform' | 'bolton';
  turnoverEur: number | null;
  /** Revenue provenance ('Reported' = filed; anything else is modelled). */
  turnoverBasis: string | null;
  /** Revenue-estimate confidence band, 'P0'..'P100'. */
  turnoverConfidenceP: string | null;
  qualityTier: string | null; // 'Strong' | 'Good' | 'Weak' | 'Not rated' | null
  qualityDirection?: string | null; // 'Improving' | 'Stable' | 'Deteriorating' | null
  sellerReadiness: number | null; // 0..100 within-market
  originationSignal: number | null; // 0..100 within-market percentile
  region: string | null;
  /** Region build kept the entity but no regulated site confirms it in-region. */
  regionUnverified?: boolean;
  /** Regulated sites on record; null = unknown (not zero). */
  siteCount: number | null;
  economicGroup?: string | null;
  specialisms?: readonly string[] | null;
  /** Median revenue of the build's candidate pool — computed in-build by the
   *  caller, never persisted (grounds "locally large" claims). */
  poolMedianRevEur?: number | null;
  /** Operators in the region carrying the same matched specialism — the v1
   *  scarcity input (supply count; demand proxies land later). */
  regionSpecialismPeers?: number | null;
}

/** Build-level evidence — assembled from the platform-builder summary. */
export interface BuildConditionEvidence {
  feasible: boolean;
  assembledEur: number;
  targetEur: number;
  memberCount: number;
  anchorName?: string | null;
  anchorRevEur?: number | null;
  regions: readonly string[];
  oversized?: boolean;
  /** Members whose revenue is modelled (basis ≠ Reported). */
  modelledRevenueCount?: number;
  /** Members kept in a region build without an in-region site confirmation. */
  regionUnverifiedCount?: number;
  /** Pool confidence from the consolidation benchmark ('low'|'medium'|'high'). */
  benchmarkConfidence?: string | null;
  benchmarkNote?: string | null;
  /** Regions the build extends into beyond the owned footprint (buyer_aware). */
  gapRegions?: readonly string[];
  /** Owned regions the build overlaps (buyer_aware). */
  overlapRegions?: readonly string[];
  /** Optional capacity inputs — the condition renders only when signals exist. */
  capacityTight?: boolean;
  capacitySignals?: ReadonlyArray<{ label: string; value: string; source?: string }>;
}

// Thresholds — coarse and ordinal on purpose (conditions read evidence, they
// are not a fourth score). SELLER_HIGH / SIGNAL_MODEST are imported from the
// blueprint grammar above.
export const SELLER_VERY_HIGH = 85; // strength bump within succession_rich
const LOCAL_CHAMPION_X = 2; // revenue ≥ 2× pool median = locally large
const LOCAL_CHAMPION_STRONG_X = 4;
const MULTI_SITE_PLATFORMLIKE = 3; // sites at which an operator reads platform-shaped
const MULTI_SITE_STRONG = 5;
const SCARCITY_PEERS_MAX = 2; // ≤2 same-specialism peers in region = scarce supply

type Ev = Condition['evidence'][number];
type Level = Condition['strength'];

const LEVEL_RANK: Record<Level, number> = { low: 0, moderate: 1, high: 2 };

const isReported = (basis: string | null | undefined) =>
  (basis ?? '').trim().toLowerCase() === 'reported';

function fmtEur(v: number): string {
  if (Math.abs(v) >= 1e6) return `€${(v / 1e6).toFixed(1).replace(/\.0$/, '')}m`;
  return `€${Math.round(v / 1e3)}k`;
}

/** Mint a condition, enforcing the two catalog-level guards: the token must
 *  allow the object kind, and there must be at least one evidence item. */
function mint(
  token: ConditionToken,
  appliesTo: ConditionAppliesTo,
  fields: {
    strength: Level;
    confidence: Level;
    tone: Condition['tone'];
    note: string;
    evidence: Ev[];
    caveats: string[];
  },
): Condition | null {
  if (!conditionAllows(token, appliesTo)) return null;
  if (fields.evidence.length === 0) return null;
  return { token, label: CONDITION_LABEL[token], appliesTo, ...fields };
}

/** Revenue provenance → confidence step-down + the caveat that must ride any
 *  condition whose claim leans on the revenue figure. */
function revenueProvenance(e: TargetConditionEvidence): {
  confidence: Level;
  caveat: string | null;
} {
  if (isReported(e.turnoverBasis)) return { confidence: 'high', caveat: null };
  if (e.turnoverBasis == null && e.turnoverEur == null)
    return { confidence: 'low', caveat: 'No revenue figure available.' };
  const band = e.turnoverConfidenceP ? ` (${e.turnoverConfidenceP})` : '';
  return { confidence: 'moderate', caveat: `Revenue is modelled${band}, not filed.` };
}

const regionCaveat = (e: TargetConditionEvidence): string | null =>
  e.regionUnverified
    ? 'In-region footprint unverified — no regulated site confirms the region.'
    : null;

const push = (arr: string[], v: string | null) => {
  if (v) arr.push(v);
};

/** Compute the conditions for one build target (spec §10). Deterministic;
 *  returns an empty array when nothing is evidenced. */
export function computeTargetConditions(
  e: TargetConditionEvidence,
  ctx: ConditionContext,
): Condition[] {
  const out: Condition[] = [];
  const rev = revenueProvenance(e);
  const buyerAware = ctx.mode === 'buyer_aware';

  // ── platform_ready — role designation + scale + quality, gated on the
  // blueprint's own hard gate (quality tier not weak).
  if (e.role === 'platform' && e.qualityTier !== 'Weak') {
    const evidence: Ev[] = [{ label: 'Prescription role', value: 'platform anchor' }];
    if (e.turnoverEur != null) {
      const vsAnchor =
        ctx.anchorMinRevEur != null && e.turnoverEur >= ctx.anchorMinRevEur
          ? ` — above the ${fmtEur(ctx.anchorMinRevEur)} anchor threshold`
          : '';
      evidence.push({ label: 'Revenue', value: `${fmtEur(e.turnoverEur)}${vsAnchor}` });
    }
    if (e.siteCount != null && e.siteCount >= 2)
      evidence.push({ label: 'Footprint', value: `${e.siteCount} regulated sites` });
    if (e.qualityTier != null && e.qualityTier !== 'Not rated')
      evidence.push({ label: 'Quality tier', value: e.qualityTier });
    const caveats: string[] = [];
    push(caveats, rev.caveat);
    if (e.siteCount == null || e.siteCount === 0)
      caveats.push('No regulated-site footprint on record.');
    if (e.qualityTier == null)
      caveats.push('No regulatory-quality signal for this market/subsegment.');
    push(caveats, regionCaveat(e));
    const strong =
      e.turnoverEur != null &&
      (ctx.anchorMinRevEur == null || e.turnoverEur >= ctx.anchorMinRevEur) &&
      (e.siteCount ?? 0) >= 2 &&
      e.qualityTier != null &&
      e.qualityTier !== 'Not rated';
    const c = mint('platform_ready', 'entity', {
      strength: strong ? 'high' : 'moderate',
      confidence: caveats.length >= 2 ? 'low' : rev.confidence,
      tone: 'positive',
      note: 'Scaled and credible as the nucleus this build assembles around.',
      evidence,
      caveats,
    });
    if (c) out.push(c);
  }

  // ── bolt_on_fit — role designation + sizing; mandate-band fit upgrades it.
  if (e.role === 'bolton') {
    const evidence: Ev[] = [{ label: 'Prescription role', value: 'bolt-on' }];
    if (e.turnoverEur != null) evidence.push({ label: 'Revenue', value: fmtEur(e.turnoverEur) });
    const inBand =
      buyerAware &&
      e.turnoverEur != null &&
      (ctx.revMinEur != null || ctx.revMaxEur != null) &&
      (ctx.revMinEur == null || e.turnoverEur >= ctx.revMinEur) &&
      (ctx.revMaxEur == null || e.turnoverEur <= ctx.revMaxEur);
    if (inBand) evidence.push({ label: 'Mandate fit', value: 'within the mandate deal-size band' });
    const caveats: string[] = [];
    push(caveats, rev.caveat);
    push(caveats, regionCaveat(e));
    const c = mint('bolt_on_fit', 'entity', {
      strength: inBand ? 'high' : 'moderate',
      confidence: rev.confidence,
      tone: 'positive',
      note: buyerAware
        ? 'Sized to plug into the anchor or an owned platform.'
        : 'Sized to plug into the build alongside comparable operators.',
      evidence,
      caveats,
    });
    if (c) out.push(c);
  }

  // ── local_champion — locally large versus the build pool's own median.
  if (
    e.region != null &&
    e.turnoverEur != null &&
    e.poolMedianRevEur != null &&
    e.poolMedianRevEur > 0 &&
    e.turnoverEur >= LOCAL_CHAMPION_X * e.poolMedianRevEur
  ) {
    const ratio = e.turnoverEur / e.poolMedianRevEur;
    const evidence: Ev[] = [
      {
        label: 'Scale vs peers',
        value: `${fmtEur(e.turnoverEur)} vs ${fmtEur(e.poolMedianRevEur)} pool median (${ratio.toFixed(1)}×)`,
      },
      { label: 'Region', value: e.region },
    ];
    if (e.siteCount != null && e.siteCount >= 2)
      evidence.push({ label: 'Footprint', value: `${e.siteCount} regulated sites` });
    const caveats: string[] = [];
    push(caveats, rev.caveat);
    push(caveats, regionCaveat(e));
    const c = mint('local_champion', 'entity', {
      strength: ratio >= LOCAL_CHAMPION_STRONG_X ? 'high' : 'moderate',
      confidence: rev.confidence,
      tone: 'positive',
      note: `Regionally meaningful operator in ${e.region} despite not being nationally large.`,
      evidence,
      caveats,
    });
    if (c) out.push(c);
  }

  // ── hidden_platform — multi-site (or grouped) operator not picked as the
  // platform: behaving like one before being labelled one. Skipped for the
  // platform pick itself, where the label would be noise.
  if (
    e.role !== 'platform' &&
    ((e.siteCount != null && e.siteCount >= MULTI_SITE_PLATFORMLIKE) || e.economicGroup != null)
  ) {
    const evidence: Ev[] = [];
    if (e.siteCount != null && e.siteCount >= MULTI_SITE_PLATFORMLIKE)
      evidence.push({ label: 'Footprint', value: `${e.siteCount} regulated sites` });
    if (e.economicGroup != null) evidence.push({ label: 'Economic group', value: e.economicGroup });
    const caveats: string[] = [];
    push(caveats, rev.caveat);
    const c = mint('hidden_platform', 'entity', {
      strength:
        (e.siteCount ?? 0) >= MULTI_SITE_STRONG ||
        (e.economicGroup != null && (e.siteCount ?? 0) >= MULTI_SITE_PLATFORMLIKE)
          ? 'high'
          : 'moderate',
      confidence: 'high', // site counts / group keys are register-derived
      tone: 'positive',
      note: 'Multi-site operator behaving like a platform before being labelled one.',
      evidence,
      caveats,
    });
    if (c) out.push(c);
  }

  // ── specialism_scarce — a mandate/build-relevant specialism with thin
  // same-specialism supply in the region. Needs the scarcity input; detection
  // provenance keeps confidence at moderate ceiling in v1.
  const matched =
    e.specialisms && ctx.mandateSpecialisms
      ? e.specialisms.filter((s) => ctx.mandateSpecialisms!.has(s))
      : [];
  if (
    matched.length > 0 &&
    e.region != null &&
    e.regionSpecialismPeers != null &&
    e.regionSpecialismPeers <= SCARCITY_PEERS_MAX
  ) {
    const c = mint('specialism_scarce', 'entity', {
      strength: e.regionSpecialismPeers === 0 ? 'high' : 'moderate',
      confidence: 'moderate',
      tone: 'positive',
      note: `Carries ${matched[0]} with thin comparable supply in ${e.region}.`,
      evidence: [
        { label: 'Specialism', value: matched.join(', ') },
        {
          label: 'Comparable operators in region',
          value: String(e.regionSpecialismPeers),
        },
      ],
      caveats: [
        'Specialism detection is heuristic (name/web-derived).',
        'Scarcity measured on supply count only — catchment demand not yet measured.',
      ],
    });
    if (c) out.push(c);
  }

  // ── quality_upgrade — two-sided by design (spec §15.4): an upside only when
  // weakness pairs with supporting positives and no value-trap flag; otherwise
  // it renders as a risk, never as a polished acquisition angle.
  if (
    e.qualityTier === 'Strong' &&
    e.originationSignal != null &&
    e.originationSignal < SIGNAL_MODEST
  ) {
    const c = mint('quality_upgrade', 'entity', {
      strength: 'moderate',
      confidence: 'high',
      tone: 'positive',
      note: 'Strong operator with modest origination visibility — value may not be priced in.',
      evidence: [
        { label: 'Quality tier', value: 'Strong' },
        {
          label: 'Origination Signal',
          value: `${Math.round(e.originationSignal)} / 100 (within-market)`,
          source: 'Origination Signal',
        },
      ],
      caveats: [],
    });
    if (c) out.push(c);
  } else if (e.qualityTier === 'Weak') {
    const deteriorating = e.qualityDirection === 'Deteriorating';
    const supported =
      (e.siteCount != null && e.siteCount >= 2) ||
      (e.turnoverEur != null &&
        e.poolMedianRevEur != null &&
        e.poolMedianRevEur > 0 &&
        e.turnoverEur >= e.poolMedianRevEur);
    const evidence: Ev[] = [{ label: 'Quality tier', value: 'Weak' }];
    if (e.qualityDirection != null)
      evidence.push({ label: 'Quality trajectory', value: e.qualityDirection });
    if (supported && !deteriorating) {
      if (e.siteCount != null && e.siteCount >= 2)
        evidence.push({ label: 'Footprint', value: `${e.siteCount} regulated sites` });
      const caveats = ['Weak quality can mark a value trap — diligence the cause.'];
      push(caveats, rev.caveat);
      const c = mint('quality_upgrade', 'entity', {
        strength: 'moderate',
        confidence: 'moderate',
        tone: 'positive',
        note: 'Professionalisation opportunity — weak rating on an otherwise substantial footprint.',
        evidence,
        caveats,
      });
      if (c) out.push(c);
    } else {
      const c = mint('quality_upgrade', 'entity', {
        strength: deteriorating ? 'high' : 'moderate',
        confidence: 'high',
        tone: 'risk',
        note: deteriorating
          ? 'Weak and deteriorating quality — possible value trap.'
          : 'Weak quality without offsetting scale — possible value trap.',
        evidence,
        caveats: [],
      });
      if (c) out.push(c);
    }
  }

  // ── succession_rich — reads the canonical Seller Readiness score at the same
  // line the succession_sale blueprint fires on.
  if (e.sellerReadiness != null && e.sellerReadiness >= SELLER_HIGH) {
    const c = mint('succession_rich', 'entity', {
      strength: e.sellerReadiness >= SELLER_VERY_HIGH ? 'high' : 'moderate',
      confidence: 'high',
      tone: 'positive',
      note: 'Owner-transition signals are unusually strong — the owner looks gettable.',
      evidence: [
        {
          label: 'Seller Readiness',
          value: `${Math.round(e.sellerReadiness)} / 100 (within-market)`,
          source: 'Seller Readiness Score',
        },
      ],
      caveats: ['Readiness is structural — not an intent-to-sell claim.'],
    });
    if (c) out.push(c);
  }

  // ── footprint_gap / cannibalisation_risk — the buyer-relative pair. In form
  // context there is no owned footprint: gap wording speaks about the build,
  // and cannibalisation never fires (a clustered build overlaps itself by
  // design — that is density, not cannibalisation).
  if (e.region != null) {
    if (buyerAware && ctx.ownedRegions.size > 0) {
      if (ctx.ownedRegions.has(e.region)) {
        const c = mint('cannibalisation_risk', 'buyer_fit', {
          strength: 'moderate',
          confidence: 'moderate',
          tone: 'risk',
          note: `Overlaps your existing ${e.region} footprint — may add cost rather than reach.`,
          evidence: [{ label: 'Region', value: `${e.region} — already in the attached portfolio` }],
          caveats: ['Overlap estimated at region grain — site catchments not compared.'],
        });
        if (c) out.push(c);
      } else {
        const caveats = [
          'Owned footprint read from the attached portfolio — it may be incomplete.',
        ];
        push(caveats, regionCaveat(e));
        const c = mint('footprint_gap', 'buyer_fit', {
          strength: 'moderate',
          confidence: e.regionUnverified ? 'moderate' : 'high',
          tone: 'positive',
          note: `Extends you into ${e.region} — no owned presence there.`,
          evidence: [
            { label: 'Region', value: e.region },
            { label: 'Owned regions', value: `${ctx.ownedRegions.size} (none is ${e.region})` },
          ],
          caveats,
        });
        if (c) out.push(c);
      }
    } else if (!buyerAware && ctx.buildRegions && !ctx.buildRegions.has(e.region)) {
      const caveats: string[] = [];
      push(caveats, regionCaveat(e));
      const c = mint('footprint_gap', 'buyer_fit', {
        strength: 'moderate',
        confidence: e.regionUnverified ? 'moderate' : 'high',
        tone: 'positive',
        note: `Extends the build into ${e.region}.`,
        evidence: [{ label: 'Region', value: `${e.region} — not otherwise covered by the build` }],
        caveats,
      });
      if (c) out.push(c);
    }
  }

  return out;
}

/** Compute the build-level conditions from the assembled summary (spec §10). */
export function computeBuildConditions(
  b: BuildConditionEvidence,
  ctx: ConditionContext,
): Condition[] {
  const out: Condition[] = [];
  const buyerAware = ctx.mode === 'buyer_aware';

  // Shared data-health caveats — ride the headline condition so the memo can
  // never present a modelled build as a filed one.
  const healthCaveats: string[] = [];
  if ((b.modelledRevenueCount ?? 0) > 0)
    healthCaveats.push(
      `${b.modelledRevenueCount} of ${b.memberCount} targets carry modelled (not filed) revenue.`,
    );
  if ((b.regionUnverifiedCount ?? 0) > 0)
    healthCaveats.push(
      `${b.regionUnverifiedCount} of ${b.memberCount} targets lack an in-region site confirmation.`,
    );
  if (b.oversized)
    healthCaveats.push('Build size exceeds a typical single-platform hold for this subsegment.');
  if ((b.benchmarkConfidence ?? '').toLowerCase() === 'low' && b.benchmarkNote)
    healthCaveats.push(`Pool confidence low — ${b.benchmarkNote}`);

  // ── platform_ready (build) — the assembled set is credible as a nucleus.
  if (b.feasible && b.memberCount >= 1 && (b.anchorName != null || b.memberCount >= 2)) {
    const evidence: Ev[] = [
      {
        label: 'Assembled',
        value: `${fmtEur(b.assembledEur)} of ${fmtEur(b.targetEur)} target across ${b.memberCount} operators`,
      },
    ];
    if (b.anchorName != null)
      evidence.push({
        label: 'Anchor',
        value:
          b.anchorRevEur != null ? `${b.anchorName} (${fmtEur(b.anchorRevEur)})` : b.anchorName,
      });
    if (b.regions.length > 0)
      evidence.push({ label: 'Footprint', value: `${b.regions.length} region(s)` });
    const c = mint('platform_ready', 'build', {
      strength: b.anchorName != null ? 'high' : 'moderate',
      confidence: (b.modelledRevenueCount ?? 0) > b.memberCount / 2 ? 'moderate' : 'high',
      tone: 'positive',
      note:
        b.anchorName != null
          ? 'Anchor-led build — a scaled nucleus with bolt-ons that fit under it.'
          : 'Aggregation build — a cluster of comparable operators that assembles to the target.',
      evidence,
      caveats: healthCaveats,
    });
    if (c) out.push(c);
  }

  // ── footprint_gap / cannibalisation_risk (build) — buyer-aware composition
  // of the member-level pair.
  if (buyerAware && (b.gapRegions?.length ?? 0) > 0) {
    const c = mint('footprint_gap', 'build', {
      strength: 'moderate',
      confidence: 'high',
      tone: 'positive',
      note: `Extends you into ${b.gapRegions!.length} region(s) with no owned presence.`,
      evidence: [{ label: 'New regions', value: b.gapRegions!.join(', ') }],
      caveats: ['Owned footprint read from the attached portfolio — it may be incomplete.'],
    });
    if (c) out.push(c);
  }
  if (buyerAware && (b.overlapRegions?.length ?? 0) > 0) {
    const c = mint('cannibalisation_risk', 'build', {
      strength: 'moderate',
      confidence: 'moderate',
      tone: 'risk',
      note: `Overlaps your existing footprint in ${b.overlapRegions!.join(', ')}.`,
      evidence: [{ label: 'Overlapping regions', value: b.overlapRegions!.join(', ') }],
      caveats: ['Overlap estimated at region grain — site catchments not compared.'],
    });
    if (c) out.push(c);
  }

  // ── capacity_constrained (build) — renders only when the caller supplies
  // actual demand/capacity signals (census/catchment wiring lands later).
  if (b.capacityTight && (b.capacitySignals?.length ?? 0) > 0) {
    const c = mint('capacity_constrained', 'build', {
      strength: 'moderate',
      confidence: 'moderate',
      tone: 'positive',
      note: 'Demand/capacity evidence suggests supply in the build footprint is tight.',
      evidence: [...b.capacitySignals!],
      caveats: ['Capacity proxies are not comparable across countries.'],
    });
    if (c) out.push(c);
  }

  return out;
}

/** Split + headline for the build summary and the memo's executive thesis
 *  (spec §10). Positives sorted strongest-first; risks kept in emission order. */
export function summariseConditions(conditions: readonly Condition[]): {
  headline: string;
  positives: Condition[];
  risks: Condition[];
} {
  const positives = conditions
    .filter((c) => c.tone === 'positive')
    .sort((a, b) => LEVEL_RANK[b.strength] - LEVEL_RANK[a.strength]);
  const risks = conditions.filter((c) => c.tone === 'risk');
  const head = positives
    .slice(0, 3)
    .map((c) => c.label)
    .join(' · ');
  const headline =
    positives.length === 0 && risks.length === 0
      ? 'No conditions established from available evidence.'
      : risks.length > 0
        ? `${head || 'No positive conditions'} — ${risks.length} risk${risks.length > 1 ? 's' : ''}: ${risks
            .map((r) => r.label)
            .join(', ')}`
        : head;
  return { headline, positives, risks };
}
